'use client';

import { useEffect, useState, useTransition } from 'react';
import { cx } from 'class-variance-authority';
import type { ts } from '@funkey/shared';
import useIsActivePath from '../../components/hooks/useIsActivePath';
import type { StoryblokBlokProps } from './utils';
import type { SB_ButtonLinkBlockProps, SB_FaqTabsBlockProps } from './types.generated';
import { StoryblokLink } from '../utils/StoryblokLink';
import { useQueryState, parseAsString } from 'nuqs';
import { TypedSearchInput } from '~/components/primitives/TypedSearchInput';
import { useI18n } from '~/features/i18n';
import { useFaqSearchStore } from './FaqSearchContext';

export const FaqTabsBlock: React.FC<StoryblokBlokProps<SB_FaqTabsBlockProps>> = props => {
    const [query, setQuery] = useQueryState(
        'q',
        parseAsString.withDefault('').withOptions({ shallow: true, throttleMs: 300 }),
    );

    const [localQuery, setLocalQuery] = useState(query);
    const [, startTransition] = useTransition();

    // Follow external query changes (e.g. browser back) in the local state
    const [syncedQuery, setSyncedQuery] = useState(query);
    if (query !== syncedQuery) {
        setSyncedQuery(query);
        setLocalQuery(query);
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value ?? '';
        setLocalQuery(value);
        startTransition(() => {
            setQuery(value || null);
        });
    };

    const i18n = useI18n();
    const store = useFaqSearchStore();
    const isSearching = query.trim().length > 0;

    // Sync search state to store so sections can hide
    useEffect(() => {
        store?.setHasQuery(isSearching);
    }, [store, isSearching]);

    // Clear the hash when searching so clearing the search doesn't scroll back to an old FAQ
    useEffect(() => {
        if (isSearching && window.location.hash) {
            window.history.replaceState(
                null,
                '',
                window.location.pathname + window.location.search,
            );
        }
    }, [isSearching]);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-row gap-2">
                {props.blok.tabs?.map(tab =>
                    !tab.url ? null : <TabButton key={tab._uid} {...tab} url={tab.url} />,
                )}
            </div>
            <div className="flex h-12 items-center rounded-full bg-white/10 px-4 ring-1 ring-white/20 transition-shadow focus-within:ring-white hover:ring-white/40">
                <TypedSearchInput
                    value={localQuery}
                    onChange={handleChange}
                    placeholder="Zoek een vraag"
                    onClear={() => {
                        setLocalQuery('');
                        startTransition(() => {
                            setQuery(null);
                        });
                    }}
                    clearLabel={i18n._('activity.search.clear', undefined, {
                        message: 'Clear search',
                    })}
                    data-testid="faq-search-input"
                    name="q"
                    textClassName="text-base"
                    iconClassName="size-4"
                    fieldClassName="flex-1"
                    className="w-full"
                />
            </div>
        </div>
    );
};

const TabButton: React.FC<
    ts.Object.override<
        { [K in keyof SB_ButtonLinkBlockProps]: SB_ButtonLinkBlockProps[K] },
        { url: NonNullable<SB_ButtonLinkBlockProps['url']> }
    >
> = props => {
    const href = `/${
        'story' in props.url && !!props.url.story ? props.url.story.full_slug : props.url.cached_url
    }`;

    return (
        <StoryblokLink
            type="link"
            link={props.url}
            className={cx(
                'rounded-full border p-3 text-sm font-medium text-white lg:px-6 lg:py-5',
                useIsActivePath(href) ? 'border-white' : 'border-transparent',
            )}
        >
            {props.text}
        </StoryblokLink>
    );
};
